import { datadogRum } from "@datadog/browser-rum";
import { useAuth, useAuthActions, useLoginWithRedirect } from "@frontegg/react";
import axios from "axios";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HyroLoader } from "@hyro-components";
import { useEffectAsync } from "@hooks";
import MainLayout from "../../layout/MainLayout";
import { api as apiAxiosInstance } from "../../services/api";
import { setAxiosTokenInterceptor } from "../../services/common";
import { registerTokenRefresh } from "../../services/tokenRefresh";
import useAppStore from "../../stores/appStore";
import {
  setPermissionsSelector,
  setUserDataSelector,
  setAccessTokenSelector
} from "../../stores/selectors/appSelectors";
import useAuthRedirect from "./hooks/Auth.hook";
import useDocsRedirects from "./hooks/Docs.hook";
import useExternalPreviewRedirect from "./hooks/ExternalPreview.hook";
import useLaunchDarklyIdentification from "./hooks/LaunchDarkly.hook";
import GenericErrorPage from "../../layout/ErrorPages/GenericErrorPage";

const InitializeApp = () => {
  const { accountId } = useParams();
  const httpReady = useAppStore(state => state.httpReady);
  const setHttpReady = useAppStore(state => state.setHttpReady);
  const setUserData = useAppStore(setUserDataSelector);
  const setPermissions = useAppStore(setPermissionsSelector);
  const setAccessToken = useAppStore(setAccessTokenSelector);
  const [hasError, setHasError] = useState(false);

  const ldClient = useLDClient();
  const { user, isAuthenticated, tenantsState } = useAuth();
  const { requestAuthorize } = useAuthActions();
  const loginWithRedirect = useLoginWithRedirect();
  const navigate = useNavigate();
  const location = useLocation();

  const pathAccountId = location?.pathname?.split("/")?.[1];

  const handleAuthRedirect = useAuthRedirect(isAuthenticated, location, loginWithRedirect);
  const handleCustomRedirects = useDocsRedirects(user, location);
  const handleExternalPreviewRedirect = useExternalPreviewRedirect(user, location);
  const handleLaunchDarklyIdentification = useLaunchDarklyIdentification(ldClient, user, pathAccountId, location);

  useEffect(() => {
    if (user?.accessToken) {
      setAccessToken(user.accessToken);
    }
  }, [user?.accessToken, setAccessToken]);

  useEffect(() => {
    registerTokenRefresh(requestAuthorize);
  }, [requestAuthorize]);

  useEffectAsync(async () => {
    try {
      if (handleAuthRedirect()) return;

      if (user?.accessToken) {
        useAppStore.getState().setAccessToken(user.accessToken);
      }

      setAxiosTokenInterceptor([apiAxiosInstance, axios]);

      if (await handleCustomRedirects()) return;
      if (handleExternalPreviewRedirect()) return;

      const redirectPath = sessionStorage.getItem("REDIRECT_AFTER_LOGIN");
      if (redirectPath) {
        sessionStorage.removeItem("REDIRECT_AFTER_LOGIN");
        navigate(redirectPath);
      }
      setPermissions(user);
      setUserData(user);

      if (tenantsState.tenants?.length > 0) {
        const accounts = tenantsState.tenants.map(tenant => ({ accountId: tenant.tenantId, name: tenant.name }));

        if (accounts.length > 0) {
          useAppStore.getState().setupAccounts(accounts);
        }
      }

      await handleLaunchDarklyIdentification();

      datadogRum.setUser({
        id: user.sub,
        name: user.name,
        email: user.email
      });

      setHttpReady(true);
    } catch (error) {
      console.error("Error in InitializeApp:", error);
      setHasError(true);
    }
  }, [
    isAuthenticated,
    loginWithRedirect,
    setPermissions,
    accountId,
    user,
    ldClient,
    tenantsState.tenants,
    location,
    navigate,
    handleAuthRedirect,
    handleCustomRedirects,
    handleLaunchDarklyIdentification
  ]);

  return hasError ? <GenericErrorPage /> : httpReady ? <MainLayout /> : <HyroLoader height="100vh" />;
};

export default InitializeApp;
